import React, { ReactNode, useRef } from 'react'
import { Tooltip } from 'primereact/tooltip'
import cn from 'classnames'
import {
    SelectButton as PrimeSelectButton,
    SelectButtonChangeEvent,
    SelectButtonPassThroughMethodOptions,
    SelectButtonProps as PrimeSelectButtonProps,
} from 'primereact/selectbutton'

export type SelectButtonOption<T> = {
    label: string
    value: T
    icon?: string
    disabled?: boolean
}

export type SelectButtonOptionState = {
    selected: boolean
    disabled: boolean
}

type FixedSelectButtonProps =
    | 'allowEmpty'
    | 'ariaLabelledBy'
    | 'className'
    | 'itemTemplate'
    | 'multiple'
    | 'onChange'
    | 'optionDisabled'
    | 'optionLabel'
    | 'options'
    | 'optionValue'
    | 'pt'
    | 'value'

type AccessibleName =
    | {
          label: string
          ariaLabel?: never
      }
    | {
          label?: never
          ariaLabel: string
      }

export type SelectButtonProps<T> = Omit<PrimeSelectButtonProps, FixedSelectButtonProps> &
    AccessibleName & {
        id: string
        value?: T | null
        options: SelectButtonOption<T>[]
        onChange: (value: T) => void
        disabledReason?: string
        className?: string
        containerClassName?: string
        fullWidth?: boolean
        optionClassName?: string | ((option: SelectButtonOption<T>, state: SelectButtonOptionState) => string)
        optionContentClassName?: string
        optionLabelClassName?: string
        optionTestId?: (option: SelectButtonOption<T>) => string
        showSelectedCheckmark?: boolean
    }

export const SelectButton = <T,>({
    id,
    label,
    ariaLabel,
    value,
    options,
    onChange,
    disabledReason,
    className,
    containerClassName,
    fullWidth = false,
    optionClassName,
    optionContentClassName,
    optionLabelClassName,
    optionTestId,
    showSelectedCheckmark = false,
    ...props
}: SelectButtonProps<T>): ReactNode => {
    const lockRef = useRef<HTMLSpanElement>(null)
    const labelId = `${id}-label`
    const disabledReasonId = `${id}-disabled-reason`
    const hasCustomOptionContent =
        showSelectedCheckmark || Boolean(optionContentClassName) || options.some((option) => option.icon)
    const hasDisabledOptions = options.some((option) => option.disabled !== undefined)

    const renderOption = (option: SelectButtonOption<T>) => (
        <span className={cn('inline-flex align-items-center gap-2', optionContentClassName)}>
            {option.icon ? <i className={option.icon} aria-hidden="true" /> : null}
            <span>{option.label}</span>
            {showSelectedCheckmark && Object.is(value, option.value) ? (
                <i className="pi pi-check" aria-hidden="true" />
            ) : null}
        </span>
    )

    const handleChange = ({ value: nextValue }: SelectButtonChangeEvent) => {
        const isOptionValue = options.some((option) => Object.is(option.value, nextValue))

        if (nextValue !== undefined && (nextValue !== null || isOptionValue)) {
            onChange(nextValue as T)
        }
    }

    const selectButton = (
        <PrimeSelectButton
            {...props}
            id={id}
            value={value}
            options={options}
            optionLabel="label"
            optionValue="value"
            optionDisabled={hasDisabledOptions ? 'disabled' : undefined}
            allowEmpty={false}
            aria-label={ariaLabel}
            aria-labelledby={label ? labelId : undefined}
            aria-describedby={props.disabled && disabledReason ? disabledReasonId : props['aria-describedby']}
            className={cn(fullWidth ? 'flex w-full' : 'inline-flex', className)}
            itemTemplate={hasCustomOptionContent ? renderOption : undefined}
            pt={{
                button: (passThroughOptions?: SelectButtonPassThroughMethodOptions) => {
                    const option = passThroughOptions?.context.option as SelectButtonOption<T>
                    const state = {
                        selected: passThroughOptions?.context.selected ?? false,
                        disabled: passThroughOptions?.context.disabled ?? false,
                    }

                    return {
                        'data-testid': optionTestId?.(option),
                        style: options.length === 1 ? { minWidth: '4.75rem' } : undefined,
                        className: cn(
                            'surface-border font-normal white-space-nowrap flex-1 justify-content-center',
                            { 'bg-green-300 text-900': state.selected },
                            typeof optionClassName === 'function' ? optionClassName(option, state) : optionClassName
                        ),
                    }
                },
                label: {
                    className: cn('font-normal', optionLabelClassName),
                },
            }}
            onChange={handleChange}
        />
    )

    const selectButtonWithDisabledReason =
        props.disabled && disabledReason ? (
            <div className={cn('inline-flex align-items-center gap-2', { 'w-full': fullWidth })}>
                {selectButton}
                <Tooltip target={lockRef} content={disabledReason} event="both" />
                <span
                    ref={lockRef}
                    tabIndex={0}
                    role="img"
                    aria-label={disabledReason}
                    className="inline-flex flex-shrink-0 align-items-center justify-content-center surface-100 border-round px-3 py-3 text-color-secondary cursor-help"
                >
                    <i className="pi pi-lock" aria-hidden="true" />
                </span>
                <span hidden id={disabledReasonId}>
                    {disabledReason}
                </span>
            </div>
        ) : (
            selectButton
        )

    if (!label) {
        return selectButtonWithDisabledReason
    }

    return (
        <div className={cn('flex flex-column', containerClassName)}>
            <span className="p-label" id={labelId}>
                {label}
            </span>
            {selectButtonWithDisabledReason}
        </div>
    )
}
